import { useState } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Chip, LoadingScreen } from '@cowoo/shared/ui-web';
import { formatBlock, formatDayShort, formatMoney, formatTime, qk, todayBerlin } from '@cowoo/shared';
import { api } from '../../lib/api';
import { EmptyState, FilterChips, ListCard, Page, Row, Table, Td, Th } from '../../ui';
import { BookingPanel } from './BookingPanel';
import { NewBookingDialog } from './NewBookingDialog';
import { CHIPS, STATUS_TONE, objectLabel, whoLabel, type BookingChip } from './bookingRow';

/** A-069 — the marker for a check-in without a matching door entry. */
const UNVERIFIED_GLYPH = '⚑';

/** § 9.1 — the range opens on this week. */
function thisWeek(): { from: string; to: string } {
  const today = new Date(`${todayBerlin()}T12:00:00Z`);
  const monday = new Date(today);
  monday.setUTCDate(today.getUTCDate() - ((today.getUTCDay() + 6) % 7));
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  return { from: monday.toISOString().slice(0, 10), to: sunday.toISOString().slice(0, 10) };
}

/**
 * `/buchungen` (10-web-admin § 9.1).
 *
 * The list is bookings **and** guest passes (OI-F-18) — the server unions them,
 * because the question this screen answers is who is in the building, and a
 * guest is. A `presenceUnverified` check-in is flagged rather than hidden:
 * staff need to know when the desk-to-person link is a claim and not an
 * observation (A-069).
 */
export function Bookings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { bookingId } = useParams();
  const creating = useMatch('/buchungen/neu') !== null;
  const [range, setRange] = useState(thisWeek);
  const [chip, setChip] = useState<BookingChip>('all');
  const [q, setQ] = useState('');

  const bookings = useQuery({
    queryKey: qk.admin.area('bookings', { ...range, chip, q }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/bookings', {
        params: { query: { from: range.from, to: range.to, chip, ...(q ? { q } : {}) } },
      });
      if (error) throw error;
      return data;
    },
  });

  if (bookings.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = bookings.data?.items ?? [];

  return (
    <Page>
      <ListCard
        title={t('admin.bookings.title')}
        actions={
          <>
            <label className="border-line rounded-input text-adminBody flex h-9 items-center gap-2 border bg-white px-3 font-semibold">
              <span className="sr-only">{t('admin.bookings.from')}</span>
              <input
                type="date"
                value={range.from}
                onChange={(event) => setRange((r) => ({ ...r, from: event.target.value }))}
                className="bg-transparent outline-none"
              />
              <span className="text-muted">–</span>
              <span className="sr-only">{t('admin.bookings.to')}</span>
              <input
                type="date"
                value={range.to}
                onChange={(event) => setRange((r) => ({ ...r, to: event.target.value }))}
                className="bg-transparent outline-none"
              />
            </label>
            <input
              value={q}
              onChange={(event) => setQ(event.target.value)}
              placeholder={t('admin.bookings.search')}
              className="border-line rounded-input text-adminBody h-9 w-[230px] border px-3"
            />
            <Button size="sm" onClick={() => navigate('/buchungen/neu')}>
              {t('admin.bookings.create')}
            </Button>
          </>
        }
      >
        <FilterChips
          value={chip}
          onChange={setChip}
          options={CHIPS.map((value) => ({
            value,
            label: t(`admin.bookings.chip.${value}`),
            count: bookings.data?.counts[value],
          }))}
        />

        {items.length === 0 ? (
          <EmptyState>{t('admin.bookings.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.bookings.column.object')}</Th>
                <Th>{t('admin.bookings.column.plan')}</Th>
                <Th>{t('admin.bookings.column.who')}</Th>
                <Th>{t('admin.bookings.column.when')}</Th>
                <Th align="right">{t('admin.bookings.column.amount')}</Th>
                <Th>{t('admin.bookings.column.status')}</Th>
              </>
            }
          >
            {items.map((booking) => (
              <Row key={booking.id} to={`/buchungen/${booking.id}`} selected={booking.id === bookingId}>
                <Td bold>{objectLabel(booking, t)}</Td>
                <Td>{booking.planName ?? '—'}</Td>
                <Td>{whoLabel(booking, t)}</Td>
                <Td>
                  {formatDayShort(booking.day)} ·{' '}
                  {booking.blockStart === null ? t('admin.bookings.allDay') : formatBlock(booking.blockStart)}
                </Td>
                <Td numeric>{booking.grossCents === null ? '—' : formatMoney(booking.grossCents)}</Td>
                <Td>
                  <Chip
                    tone={STATUS_TONE[booking.status] ?? 'neutral'}
                    label={
                      booking.checkedInAt && booking.status === 'checked_in'
                        ? t('admin.bookings.checkedInAt', { time: formatTime(booking.checkedInAt) })
                        : t(`admin.bookings.state.${booking.status}`)
                    }
                  />
                  {booking.presenceUnverified ? (
                    // A glyph, not copy: the meaning is in the accessible name.
                    <span
                      className="text-warn-text ml-2"
                      role="img"
                      aria-label={t('admin.bookings.unverified')}
                      title={t('admin.bookings.unverified')}
                    >
                      {UNVERIFIED_GLYPH}
                    </span>
                  ) : null}
                </Td>
              </Row>
            ))}
          </Table>
        )}
      </ListCard>

      {bookingId && bookingId !== 'neu' ? (
        <BookingPanel id={bookingId} onClose={() => navigate('/buchungen')} />
      ) : null}
      {creating ? <NewBookingDialog onClose={() => navigate('/buchungen')} /> : null}
    </Page>
  );
}
